import { ArrowRight, CheckCircle2, Sparkles, UserCircle } from '../components/icons';
import { useEffect, useMemo, useRef, useState, type ChangeEvent, type ComponentType, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import * as XLSX from 'xlsx';
import Button from '../components/Button';
import Card from '../components/Card';
import { useAuth } from '../store/authContext';
import { useToast } from '../store/toastContext';

const AI_WEBHOOK_URL = 'https://apiwhattsdom-n8n.znwkmn.easypanel.host/webhook/31acfea2-2b4b-4b2e-88a7-293aefc0fc4d';
const AI_HISTORY_WEBHOOK_URL = 'https://apiwhattsdom-n8n.znwkmn.easypanel.host/webhook/8abdbd93-0843-424c-93a9-eb08f5036749';
const EXCEL_IMPORT_WEBHOOK_URL = 'https://apiwhattsdom-n8n.znwkmn.easypanel.host/webhook/78fd29a4-4c21-4abd-b45d-b0a49f27ca85';
const EXCEL_COLUMNS = ['nome_empresa', 'telefone', 'endereco', 'website', 'email', 'rating', 'reviews'] as const;

type ExcelColumn = (typeof EXCEL_COLUMNS)[number];
type EmpresaExcelRow = Record<ExcelColumn, string>;
const Presence = AnimatePresence as ComponentType<{ children?: ReactNode }>;
type ExcelImportEmpresaItem = Partial<EmpresaExcelRow>;
type ExcelImportApiItem = {
  resultado?: {
    inseridos?: ExcelImportEmpresaItem[];
    ja_cadastrados?: ExcelImportEmpresaItem[];
    total_inseridos?: number;
    total_recebidos?: number;
    total_ja_cadastrados?: number;
  } | null;
};
type ExcelImportSummary = {
  inseridos: EmpresaExcelRow[];
  jaCadastrados: EmpresaExcelRow[];
  totalInseridos: number;
  totalRecebidos: number;
  totalJaCadastrados: number;
};

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
};

type ConversationHistoryItem = {
  id?: string;
  text_gpt?: string | null;
  text_human?: string | null;
  user_id?: string;
  created_at?: string;
};

type ConversationHistoryResponse = {
  data?: ConversationHistoryItem[];
};

const HEADER_ALIASES: Record<string, ExcelColumn> = {
  nome_empresa: 'nome_empresa',
  nomeempresa: 'nome_empresa',
  empresa: 'nome_empresa',
  nome: 'nome_empresa',
  telefone: 'telefone',
  whatsapp: 'telefone',
  telefonewhatsapp: 'telefone',
  endereco: 'endereco',
  enderecoempresa: 'endereco',
  website: 'website',
  site: 'website',
  url: 'website',
  email: 'email',
  e_mail: 'email',
  mail: 'email',
  rating: 'rating',
  nota: 'rating',
  reviews: 'reviews',
  review: 'reviews',
  avaliacoes: 'reviews',
  avaliacao: 'reviews'
};

const WELCOME_MESSAGE: ChatMessage = {
  id: 'welcome-assistant',
  role: 'assistant',
  text: 'Oi! Sou seu assistente de busca. Me diga o nicho e a cidade que eu monto a estratégia para você.'
};

const createEmptyEmpresaExcelRow = (): EmpresaExcelRow => ({
  nome_empresa: '',
  telefone: '',
  endereco: '',
  website: '',
  email: '',
  rating: '',
  reviews: ''
});

const normalizeHeader = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

const normalizeCellValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value).trim();
  return '';
};

const normalizeEmpresaImportItem = (item: ExcelImportEmpresaItem | null | undefined): EmpresaExcelRow => {
  const row = createEmptyEmpresaExcelRow();
  if (!item) return row;

  EXCEL_COLUMNS.forEach((column) => {
    row[column] = normalizeCellValue(item[column]);
  });

  return row;
};

const normalizeIdentityValue = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '');

const createEmpresaImportKey = (item: EmpresaExcelRow): string =>
  [
    normalizeIdentityValue(item.website),
    normalizeIdentityValue(item.email),
    normalizeIdentityValue(item.nome_empresa)
  ].join('||');

const dedupeEmpresaImportRows = (items: EmpresaExcelRow[]): EmpresaExcelRow[] => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = createEmpresaImportKey(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const filterOutRowsByKey = (items: EmpresaExcelRow[], blockedKeys: Set<string>): EmpresaExcelRow[] =>
  items.filter((item) => !blockedKeys.has(createEmpresaImportKey(item)));

const buildExcelImportSummary = (payload: unknown): ExcelImportSummary => {
  const responseItems = (Array.isArray(payload) ? payload : [payload]) as ExcelImportApiItem[];

  const inseridos = dedupeEmpresaImportRows(
    responseItems.flatMap((item) => (Array.isArray(item?.resultado?.inseridos) ? item.resultado!.inseridos! : []).map((entry) => normalizeEmpresaImportItem(entry)))
  );
  const inseridosKeys = new Set(inseridos.map((item) => createEmpresaImportKey(item)));
  const jaCadastrados = filterOutRowsByKey(
    dedupeEmpresaImportRows(
      responseItems.flatMap((item) =>
        (Array.isArray(item?.resultado?.ja_cadastrados) ? item.resultado!.ja_cadastrados! : []).map((entry) => normalizeEmpresaImportItem(entry))
      )
    ),
    inseridosKeys
  );
  const totalRecebidosUnicos = dedupeEmpresaImportRows([...inseridos, ...jaCadastrados]).length;

  const totalInseridosInformado = responseItems.reduce((acc, item) => Math.max(acc, Number(item?.resultado?.total_inseridos || 0)), 0);
  const totalRecebidosInformado = responseItems.reduce((acc, item) => Math.max(acc, Number(item?.resultado?.total_recebidos || 0)), 0);
  const totalJaCadastradosInformado = responseItems.reduce((acc, item) => Math.max(acc, Number(item?.resultado?.total_ja_cadastrados || 0)), 0);

  return {
    inseridos,
    jaCadastrados,
    totalInseridos: Math.min(Math.max(totalInseridosInformado, inseridos.length), totalRecebidosUnicos || inseridos.length),
    totalRecebidos: totalRecebidosUnicos || totalRecebidosInformado,
    totalJaCadastrados: Math.min(Math.max(totalJaCadastradosInformado, jaCadastrados.length), totalRecebidosUnicos || jaCadastrados.length)
  };
};

const parseEmpresaRowsFromWorkbook = (workbook: XLSX.WorkBook): EmpresaExcelRow[] => {
  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) return [];

  const worksheet = workbook.Sheets[firstSheetName];
  const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, { defval: '' });

  return rawRows
    .map((rawRow) => {
      const parsedRow = createEmptyEmpresaExcelRow();

      Object.entries(rawRow).forEach(([header, value]) => {
        const normalizedHeader = normalizeHeader(header);
        const targetColumn = HEADER_ALIASES[normalizedHeader];
        if (!targetColumn) return;
        parsedRow[targetColumn] = normalizeCellValue(value);
      });

      return parsedRow;
    })
    .filter((row) => EXCEL_COLUMNS.some((column) => row[column]));
};

const downloadExcelTemplate = () => {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet([createEmptyEmpresaExcelRow()], {
    header: [...EXCEL_COLUMNS]
  });

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Empresas');
  XLSX.writeFile(workbook, 'modelo-empresas-busca-ia.xlsx');
};

const fetchHistory = async (token: string): Promise<Response> => {
  const payload = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token })
  } as const;

  const firstTry = await fetch(AI_HISTORY_WEBHOOK_URL, payload);
  if (firstTry.status !== 404) return firstTry;

  // n8n webhook-test pode retornar 404 fora do modo de teste; tenta o endpoint de produção.
  const productionHistoryUrl = AI_HISTORY_WEBHOOK_URL.replace('/webhook-test/', '/webhook/');
  return fetch(productionHistoryUrl, payload);
};

const readTextFromPayload = (payload: unknown): string => {
  if (typeof payload === 'string') return payload.trim();
  if (!payload) return '';
  if (Array.isArray(payload)) {
    for (const item of payload) {
      const text = readTextFromPayload(item);
      if (text) return text;
    }
    return '';
  }
  if (typeof payload !== 'object') return '';

  const source = payload as Record<string, unknown>;
  const directCandidates = [
    source.text_gpt,
    source.resposta,
    source.response,
    source.mensagem,
    source.text,
    source.output,
    source.answer,
    source.reply,
    source.content
  ];
  for (const value of directCandidates) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }

  const nestedCandidates = [
    source.message,
    source.response,
    source.data,
    source.result,
    source.payload,
    source.choices,
    source.delta
  ];
  for (const nested of nestedCandidates) {
    const nestedText = readTextFromPayload(nested);
    if (nestedText) return nestedText;
  }

  try {
    return JSON.stringify(payload);
  } catch {
    return '';
  }
};

export default function BuscaAiPage() {
  const { session } = useAuth();
  const { pushToast } = useToast();
  const [sending, setSending] = useState(false);
  const [readingExcel, setReadingExcel] = useState(false);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [excelUploadProgress, setExcelUploadProgress] = useState(0);
  const [excelImportSummary, setExcelImportSummary] = useState<ExcelImportSummary | null>(null);
  const [showExcelTools, setShowExcelTools] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState('');
  const [draftMessage, setDraftMessage] = useState('');
  const [excelFileName, setExcelFileName] = useState('');
  const [excelRows, setExcelRows] = useState<EmpresaExcelRow[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE]);
  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const excelInputRef = useRef<HTMLInputElement | null>(null);
  const userId = session?.user?.id?.trim() || '';

  const userName = useMemo(() => session?.user?.name?.split(' ')[0] || 'Você', [session?.user?.name]);
  const particles = useMemo(
    () =>
      Array.from({ length: 12 }).map((_, index) => ({
        id: `particle-${index}`,
        left: `${8 + ((index * 17) % 84)}%`,
        top: `${10 + ((index * 13) % 78)}%`,
        delay: `${(index % 7) * 0.45}s`,
        duration: `${4 + (index % 5) * 0.9}s`
      })),
    []
  );
  const excelPreviewRows = useMemo(() => excelRows.slice(0, 5), [excelRows]);
  const insertedPreviewRows = useMemo(() => excelImportSummary?.inseridos.slice(0, 6) || [], [excelImportSummary]);
  const alreadyRegisteredPreviewRows = useMemo(() => excelImportSummary?.jaCadastrados.slice(0, 6) || [], [excelImportSummary]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending]);

  useEffect(() => {
    if (!uploadingExcel) return;

    setExcelUploadProgress(4);
    const intervalId = window.setInterval(() => {
      setExcelUploadProgress((current) => {
        if (current >= 92) return current;
        return Math.min(current + (current < 40 ? 8 : current < 70 ? 5 : 3), 92);
      });
    }, 180);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [uploadingExcel]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const loadHistory = async () => {
      setHistoryLoading(true);
      try {
        const response = await fetchHistory(userId);
        const raw = (await response.json().catch(() => null)) as ConversationHistoryResponse | null;
        if (!response.ok) {
          throw new Error(`Falha ao carregar historico (${response.status}).`);
        }

        const responseData = raw?.data;
        const historyItems = Array.isArray(responseData) ? responseData : [];
        const orderedHistory = [...historyItems].sort((a, b) => {
          const aTime = new Date(a.created_at || '').getTime();
          const bTime = new Date(b.created_at || '').getTime();
          return aTime - bTime;
        });

        const historyMessages: ChatMessage[] = [];
        orderedHistory.forEach((item, index) => {
          const historyId = item.id?.trim() || `history-${index}`;
          const humanText = (item.text_human || '').trim();
          const gptText = (item.text_gpt || '').trim();
          if (humanText) {
            historyMessages.push({
              id: `history-user-${historyId}`,
              role: 'user',
              text: humanText
            });
          }
          if (gptText) {
            historyMessages.push({
              id: `history-assistant-${historyId}`,
              role: 'assistant',
              text: gptText
            });
          }
        });

        if (!cancelled) {
          setMessages(historyMessages.length ? [WELCOME_MESSAGE, ...historyMessages] : [WELCOME_MESSAGE]);
        }
      } catch (error) {
        if (!cancelled) {
          pushToast({ type: 'error', title: error instanceof Error ? error.message : 'Nao foi possivel carregar o historico.' });
        }
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    };

    void loadHistory();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleCopyMessage = async (messageId: string, text: string) => {
    const value = text.trim();
    if (!value) return;
    const pageScrollY = window.scrollY;
    const chatScrollTop = listRef.current?.scrollTop ?? null;
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('clipboard_unavailable');
      }
      await navigator.clipboard.writeText(value);

      setCopiedMessageId(messageId);
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: pageScrollY, behavior: 'auto' });
        if (listRef.current && chatScrollTop !== null) {
          listRef.current.scrollTop = chatScrollTop;
        }
      });
      window.setTimeout(() => {
        setCopiedMessageId((current) => (current === messageId ? '' : current));
      }, 1400);
    } catch {
      pushToast({ type: 'error', title: 'Nao foi possivel copiar o texto.' });
    }
  };

  const sendMessage = async () => {
    const mensagem = draftMessage.trim();
    if (!mensagem) return;
    if (!userId) {
      pushToast({ type: 'error', title: 'Sessao invalida. Faca login novamente.' });
      return;
    }

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      text: mensagem
    };
    setMessages((prev) => [...prev, userMessage]);
    setDraftMessage('');
    setSending(true);

    try {
      const response = await fetch(AI_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mensagem,
          id: userId
        })
      });

      const raw = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(`Falha ao consultar IA (${response.status}).`);
      }

      const answer = readTextFromPayload(raw) || 'Recebi sua pergunta. Ajuste os detalhes (nicho, cidade e porte) para uma resposta mais precisa.';
      setMessages((prev) => [
        ...prev,
        {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          text: answer
        }
      ]);
    } catch (error) {
      pushToast({ type: 'error', title: error instanceof Error ? error.message : 'Nao foi possivel consultar a IA.' });
    } finally {
      setSending(false);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleExcelFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setReadingExcel(true);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const parsedRows = parseEmpresaRowsFromWorkbook(workbook);

      if (!parsedRows.length) {
        throw new Error('Nao encontrei linhas validas no arquivo. Use as colunas: nome_empresa, telefone, endereco, website, email, rating e reviews.');
      }

      setExcelRows(parsedRows);
      setExcelFileName(file.name);
      setExcelImportSummary(null);
      pushToast({ type: 'success', title: `${parsedRows.length} linha(s) lida(s) do arquivo.` });
    } catch (error) {
      setExcelRows([]);
      setExcelFileName('');
      pushToast({ type: 'error', title: error instanceof Error ? error.message : 'Nao foi possivel ler o Excel.' });
    } finally {
      setReadingExcel(false);
      event.target.value = '';
    }
  };

  const uploadExcelRows = async () => {
    if (!excelRows.length) {
      pushToast({ type: 'error', title: 'Importe um arquivo antes de enviar.' });
      return;
    }
    if (!userId) {
      pushToast({ type: 'error', title: 'Sessao invalida. Faca login novamente.' });
      return;
    }

    setUploadingExcel(true);
    setExcelUploadProgress(0);
    try {
      const response = await fetch(EXCEL_IMPORT_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify([
          {
            route: 'insert_empresa_com_execel',
            token: userId,
            id: userId,
            data: excelRows
          }
        ])
      });

      const raw = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(`Falha ao enviar planilha (${response.status}).`);
      }

      const summary = buildExcelImportSummary(raw);
      setExcelImportSummary(summary);
      setExcelUploadProgress(100);

      pushToast({
        type: 'success',
        title:
          summary.totalInseridos || summary.totalJaCadastrados
            ? `Upload concluido. ${summary.totalInseridos} inserido(s) e ${summary.totalJaCadastrados} ja cadastrado(s).`
            : 'Planilha enviada com sucesso.'
      });
    } catch (error) {
      setExcelUploadProgress(0);
      pushToast({ type: 'error', title: error instanceof Error ? error.message : 'Nao foi possivel enviar a planilha.' });
    } finally {
      setUploadingExcel(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-5xl space-y-5">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <Card className="ai-chat-hero relative overflow-hidden p-5">
          <div className="ai-chat-sheen" />
          {particles.map((particle) => (
            <span
              key={particle.id}
              className="ai-chat-particle"
              style={{
                left: particle.left,
                top: particle.top,
                animationDelay: particle.delay,
                animationDuration: particle.duration
              }}
            />
          ))}
        <div className="ai-chat-grid-overlay" />
        <div className="ai-chat-hero-glow ai-chat-hero-glow-a" />
        <div className="ai-chat-hero-glow ai-chat-hero-glow-b" />
        <div className="ai-chat-hero-ring ai-chat-hero-ring-a" />
        <div className="ai-chat-hero-ring ai-chat-hero-ring-b" />
        <div className="relative z-10 flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Busca Inteligente</p>
            <h1 className="mt-1 text-2xl font-black">Conversa com IA</h1>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Faça perguntas de prospecção e estratégia. O sistema usa seu usuário logado automaticamente.</p>
          </div>
          <div className="ai-chat-orb">
            <Sparkles className="h-5 w-5" />
          </div>
        </div>
        </Card>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.36, delay: 0.04 }}>
        <Card className="space-y-4 p-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="space-y-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Importacao por Excel</p>
                <h2 className="mt-1 text-xl font-black text-slate-900 dark:text-slate-100">Baixe o modelo, preencha e envie para o webhook</h2>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                A planilha precisa conter estas colunas: <code>nome_empresa</code>, <code>telefone</code>, <code>endereco</code>, <code>website</code>, <code>email</code>, <code>rating</code> e <code>reviews</code>.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setShowExcelTools((current) => !current)}>
                {showExcelTools ? 'Esconder importacao' : 'Mostrar importacao'}
              </Button>
            </div>
          </div>

          {showExcelTools ? (
            <>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={downloadExcelTemplate}>
                  Baixar modelo Excel
                </Button>
                <Button variant="ghost" onClick={() => excelInputRef.current?.click()} disabled={readingExcel}>
                  {readingExcel ? 'Lendo arquivo...' : 'Selecionar arquivo'}
                </Button>
                <Button variant="cta" onClick={() => void uploadExcelRows()} disabled={uploadingExcel || !excelRows.length}>
                  {uploadingExcel ? 'Enviando planilha...' : 'Enviar para webhook'}
                </Button>
              </div>

              <input
                ref={excelInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={(event) => void handleExcelFileChange(event)}
              />

              <div className="rounded-2xl border border-dashed border-borderLight bg-slate-50/70 p-4 dark:border-borderDark dark:bg-slate-900/50">
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-100">{excelFileName ? `Arquivo selecionado: ${excelFileName}` : 'Nenhum arquivo selecionado.'}</p>
                {excelRows.length ? (
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                    <span className="rounded-full bg-white px-2 py-1 dark:bg-slate-950">{excelRows.length} linha(s) pronta(s) para envio</span>
                    <button
                      type="button"
                      onClick={() => {
                        setExcelRows([]);
                        setExcelFileName('');
                      }}
                      className="rounded-full border border-borderLight px-2 py-1 font-semibold transition hover:bg-white dark:border-borderDark dark:hover:bg-slate-950"
                    >
                      Limpar importacao
                    </button>
                  </div>
                ) : null}
              </div>

              {uploadingExcel || excelImportSummary ? (
                <div className="rounded-2xl border border-borderLight bg-white p-4 dark:border-borderDark dark:bg-slate-950">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                        {uploadingExcel ? 'Processando planilha...' : 'Resultado da importacao'}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-300">
                        {uploadingExcel
                          ? 'Estamos enviando os dados e acompanhando o processamento.'
                          : 'Aqui esta um resumo amigavel do retorno do webhook.'}
                      </p>
                    </div>
                    <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">{excelUploadProgress}%</span>
                  </div>

                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-primary via-sky-500 to-cta transition-all duration-300"
                      style={{ width: `${excelUploadProgress}%` }}
                    />
                  </div>

                  {excelImportSummary ? (
                    <>
                      <div className="mt-4 grid gap-3 md:grid-cols-3">
                        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900 dark:bg-emerald-950/40">
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">Inseridos</p>
                          <p className="mt-1 text-2xl font-black text-emerald-800 dark:text-emerald-100">{excelImportSummary.totalInseridos}</p>
                        </div>
                        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/40">
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700 dark:text-amber-300">Ja cadastrados</p>
                          <p className="mt-1 text-2xl font-black text-amber-800 dark:text-amber-100">{excelImportSummary.totalJaCadastrados}</p>
                        </div>
                        <div className="rounded-2xl border border-sky-200 bg-sky-50 p-3 dark:border-sky-900 dark:bg-sky-950/40">
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700 dark:text-sky-300">Total recebido</p>
                          <p className="mt-1 text-2xl font-black text-sky-800 dark:text-sky-100">{excelImportSummary.totalRecebidos}</p>
                        </div>
                      </div>

                      <div className="mt-4 grid gap-4 lg:grid-cols-2">
                        <div className="rounded-2xl border border-borderLight p-3 dark:border-borderDark">
                          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Empresas inseridas</p>
                          {insertedPreviewRows.length ? (
                            <div className="mt-3 space-y-2">
                              {insertedPreviewRows.map((item, index) => (
                                <div key={`${item.nome_empresa}-${item.website}-${index}`} className="rounded-xl bg-slate-50 p-3 text-sm dark:bg-slate-900">
                                  <p className="font-semibold text-slate-800 dark:text-slate-100">{item.nome_empresa || 'Empresa sem nome'}</p>
                                  <p className="text-xs text-slate-500 dark:text-slate-300">{item.telefone || 'Sem telefone'} {item.email ? `• ${item.email}` : ''}</p>
                                  <p className="text-xs text-slate-500 dark:text-slate-300">{item.website || 'Sem website'}</p>
                                </div>
                              ))}
                              {excelImportSummary.inseridos.length > insertedPreviewRows.length ? (
                                <p className="text-xs text-slate-500 dark:text-slate-300">
                                  E mais {excelImportSummary.inseridos.length - insertedPreviewRows.length} empresa(s) inserida(s).
                                </p>
                              ) : null}
                            </div>
                          ) : (
                            <p className="mt-2 text-sm text-slate-500 dark:text-slate-300">Nenhuma empresa nova foi inserida nesta importacao.</p>
                          )}
                        </div>

                        <div className="rounded-2xl border border-borderLight p-3 dark:border-borderDark">
                          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Empresas que ja existiam</p>
                          {alreadyRegisteredPreviewRows.length ? (
                            <div className="mt-3 space-y-2">
                              {alreadyRegisteredPreviewRows.map((item, index) => (
                                <div key={`${item.nome_empresa}-${item.website}-${index}`} className="rounded-xl bg-slate-50 p-3 text-sm dark:bg-slate-900">
                                  <p className="font-semibold text-slate-800 dark:text-slate-100">{item.nome_empresa || 'Empresa sem nome'}</p>
                                  <p className="text-xs text-slate-500 dark:text-slate-300">{item.telefone || 'Sem telefone'} {item.email ? `• ${item.email}` : ''}</p>
                                  <p className="text-xs text-slate-500 dark:text-slate-300">{item.website || 'Sem website'}</p>
                                </div>
                              ))}
                              {excelImportSummary.jaCadastrados.length > alreadyRegisteredPreviewRows.length ? (
                                <p className="text-xs text-slate-500 dark:text-slate-300">
                                  E mais {excelImportSummary.jaCadastrados.length - alreadyRegisteredPreviewRows.length} empresa(s) que ja estavam cadastrada(s).
                                </p>
                              ) : null}
                            </div>
                          ) : (
                            <p className="mt-2 text-sm text-slate-500 dark:text-slate-300">Nenhuma empresa duplicada foi retornada pelo webhook.</p>
                          )}
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
              ) : null}

              <div className="overflow-hidden rounded-2xl border border-borderLight dark:border-borderDark">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-borderLight text-left text-sm dark:divide-borderDark">
                    <thead className="bg-slate-100/80 dark:bg-slate-900">
                      <tr>
                        {EXCEL_COLUMNS.map((column) => (
                          <th key={column} className="px-3 py-2 font-semibold text-slate-700 dark:text-slate-200">
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-borderLight bg-white dark:divide-borderDark dark:bg-slate-950">
                      {excelPreviewRows.length ? (
                        excelPreviewRows.map((row, index) => (
                          <tr key={`${row.nome_empresa}-${index}`}>
                            {EXCEL_COLUMNS.map((column) => (
                              <td key={`${column}-${index}`} className="max-w-[240px] px-3 py-2 text-slate-600 dark:text-slate-300">
                                <span className="block truncate">{row[column] || '-'}</span>
                              </td>
                            ))}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={EXCEL_COLUMNS.length} className="px-3 py-6 text-center text-slate-500 dark:text-slate-300">
                            Importe um arquivo para visualizar as primeiras linhas aqui.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-2xl border border-dashed border-borderLight bg-slate-50/70 p-4 text-sm text-slate-500 dark:border-borderDark dark:bg-slate-900/50 dark:text-slate-300">
              A area de importacao ficou escondida por padrao para nao atrapalhar a visualizacao do chat.
            </div>
          )}
        </Card>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.06 }}>
        <Card className={`ai-chat-panel space-y-4 p-4 ${sending ? 'ai-chat-panel-live' : ''}`}>
        <div ref={listRef} className="ai-chat-scroll h-[calc(100dvh-24rem)] min-h-[320px] max-h-[72vh] space-y-3 overflow-y-auto pr-1">
          {historyLoading ? <p className="text-xs text-slate-500 dark:text-slate-300">Carregando historico...</p> : null}
          <Presence>
            {messages.map((message) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 10, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -6, scale: 0.98 }}
                transition={{ duration: 0.18 }}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`ai-chat-bubble w-[680px] max-w-[92vw] rounded-2xl border px-3 py-2 text-sm shadow-sm ${
                    message.role === 'user'
                      ? 'border-primary/40 bg-primary/10 text-slate-900 dark:border-primary/50 dark:bg-primary/20 dark:text-slate-100'
                      : 'border-borderLight bg-white text-slate-700 dark:border-borderDark dark:bg-slate-900 dark:text-slate-200'
                  }`}
                >
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <p className="inline-flex items-center gap-1 text-[11px] font-semibold opacity-75">
                      {message.role === 'user' ? <UserCircle className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
                      {message.role === 'user' ? userName : 'Assistente'}
                    </p>
                    <button
                      type="button"
                      tabIndex={-1}
                      onPointerDown={(event) => {
                        event.preventDefault();
                      }}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onClick={() => void handleCopyMessage(message.id, message.text)}
                      className="inline-flex items-center gap-1 rounded-md border border-transparent px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide opacity-70 transition hover:border-borderLight hover:bg-white/70 hover:opacity-100 dark:hover:border-borderDark dark:hover:bg-slate-800"
                      title="Copiar mensagem"
                    >
                      {copiedMessageId === message.id ? <CheckCircle2 className="h-3.5 w-3.5" /> : null}
                      {copiedMessageId === message.id ? 'Copiado' : 'Copiar'}
                    </button>
                  </div>
                  <div className="ai-chat-bubble-content">
                    <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{message.text}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </Presence>
          {sending ? (
            <div className="flex justify-start">
              <div className="ai-chat-typing rounded-2xl border border-borderLight bg-white px-3 py-2 dark:border-borderDark dark:bg-slate-900">
                <span />
                <span />
                <span />
              </div>
            </div>
          ) : null}
        </div>

        <div className="rounded-2xl border border-borderLight bg-white p-3 dark:border-borderDark dark:bg-slate-900">
          <textarea
            ref={inputRef}
            value={draftMessage}
            onChange={(event) => setDraftMessage(event.target.value)}
            onKeyDown={(event) => {
              if ((event.nativeEvent as unknown as { isComposing?: boolean }).isComposing) return;
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                if (!sending) void sendMessage();
              }
            }}
            rows={5}
            placeholder="Ex.: oi bom dia, qual seria tds as empresas metalurgicas para minha empresa?"
            className="h-32 w-full resize-none overflow-y-auto bg-transparent text-sm outline-none"
          />
          <div className="mt-3 flex items-center justify-between gap-2">
            <p className="text-[11px] text-slate-500 dark:text-slate-300">Enter envia. Shift+Enter quebra linha.</p>
            <Button
              variant="ghost"
              className="h-9 rounded-lg px-3 text-xs"
              disabled={sending || clearing || messages.length <= 1}
              onClick={() => {
                setClearing(true);
                window.setTimeout(() => {
                  setMessages((prev) => prev.slice(0, 1));
                  setDraftMessage('');
                  setClearing(false);
                }, 120);
              }}
            >
              {clearing ? 'Limpando...' : 'Limpar conversa'}
            </Button>
            <Button variant="cta" onClick={() => void sendMessage()} disabled={sending} iconLeft={<ArrowRight className="h-4 w-4" />}>
              {sending ? 'Consultando...' : 'Enviar'}
            </Button>
          </div>
        </div>
        </Card>
      </motion.div>
    </div>
  );
}
